import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import api from '../../utils/api';
import Footer from '../../components/Footer/Footer';
import RegistrationDecisionDialog from '../../components/Registrations/RegistrationDecisionDialog';
import { toast } from 'react-toastify';

const fontBase = { fontFamily: 'Roboto, Arial, sans-serif' };

const labelSx = { ...fontBase, color: '#6b7280', fontSize: '12px', mb: 0.5 };
const valueSx = { ...fontBase, color: '#111827', fontSize: '14px', wordBreak: 'break-word' };

const NOT_PROVIDED = (
  <Typography component="span" sx={{ ...fontBase, color: '#9ca3af', fontSize: '14px' }}>
    Not provided
  </Typography>
);

const Field = ({ label, value, xs = 12, sm = 6 }) => (
  <Grid item xs={xs} sm={sm}>
    <Typography sx={labelSx}>{label}</Typography>
    <Typography sx={valueSx} component="div">{value || NOT_PROVIDED}</Typography>
  </Grid>
);

const Section = ({ title, children }) => (
  <Paper
    elevation={0}
    sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: { xs: 2, sm: 3 }, mb: 3 }}
  >
    <Typography sx={{ ...fontBase, fontWeight: 600, fontSize: '15px', color: '#111827', mb: 2 }}>
      {title}
    </Typography>
    <Grid container spacing={2.5}>
      {children}
    </Grid>
  </Paper>
);

const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const formatAddress = (address) => {
  if (!address) return '';
  return [address.street, address.city, address.country, address.postalCode]
    .filter(Boolean)
    .join(', ');
};

// The profile stores these as snake_case enum values; procurement should not
// have to read them that way.
const humanise = (value) =>
  String(value || '')
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * Everything a procurement specialist needs to decide on one supplier
 * registration.
 *
 * The queue used to approve and reject straight off a table row showing four
 * columns; the company details the decision actually rests on were loaded to
 * build that row and then discarded. This page shows the whole submission and
 * puts the decision at the bottom of it.
 */
const RegistrationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [decisionStatus, setDecisionStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchRegistration = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/users/pending-registrations/${id}`);
      setRecord(response.data.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load this registration');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRegistration();
  }, [fetchRegistration]);

  const submitDecision = async ({ status, comment, department }) => {
    try {
      setSubmitting(true);
      await api.put(`/users/${id}/supplier-approval`, {
        status,
        comment,
        ...(department ? { department } : {}),
      });
      toast.success(`Registration ${status} successfully`);
      setDecisionStatus(null);
      navigate('/registrations');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update registration');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !record) {
    return (
      <Container maxWidth="md" sx={{ py: 5 }}>
        <Alert severity="error" sx={{ ...fontBase }}>{error || 'Registration not found'}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/registrations')}
          sx={{ ...fontBase, textTransform: 'none', mt: 2, color: '#374151' }}
        >
          Back to registrations
        </Button>
      </Container>
    );
  }

  const { user, profile, profileComplete, missing, duplicates = [] } = record;
  // A registration-number match is what the approval route refuses; a name
  // match is only a hint.
  const heldBy = duplicates.find((d) => d.matchedOn === 'registration_number');
  const sameName = duplicates.filter((d) => d.matchedOn === 'company_name');
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
  const contact = profile?.authorizedPerson || {};
  const decided = user.supplierApprovalStatus && user.supplierApprovalStatus !== 'pending';

  // The approval route refuses an incomplete profile outright. Say so here
  // rather than letting the specialist click into that error.
  const blockedReason = !user.isEmailVerified
    ? 'This supplier has not verified their email address yet, so they cannot be onboarded.'
    : !profileComplete
      ? `This supplier has not finished their profile. Still missing: ${(missing || []).join(', ')}.`
      : heldBy
        ? `This company already has an account: ${heldBy.email} (${heldBy.supplierName || 'no name'}, registration ${heldBy.companyRegistrationNumber}). `
          + 'Only one account per company is allowed. Reject this registration, or deactivate the other account first.'
        : '';

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="lg" sx={{ pt: { xs: 3, sm: 5 }, pb: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 }, flex: 1 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/registrations')}
          sx={{ ...fontBase, textTransform: 'none', mb: 2, color: '#374151', px: 0 }}
        >
          Back to registrations
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
          <Typography
            variant="h4"
            sx={{ ...fontBase, fontWeight: 'bold', color: '#111827', fontSize: { xs: '22px', sm: '28px' } }}
          >
            {profile?.supplierName || fullName || 'Supplier registration'}
          </Typography>
          {decided ? (
            <Chip
              label={humanise(user.supplierApprovalStatus)}
              size="small"
              sx={{
                backgroundColor: user.supplierApprovalStatus === 'approved' ? '#dcfce7' : '#fef2f2',
                color: user.supplierApprovalStatus === 'approved' ? '#166534' : '#991b1b',
                fontWeight: 500, fontSize: '13px', height: '24px', borderRadius: '12px',
              }}
            />
          ) : (
            <Chip
              label="Pending Approval"
              size="small"
              sx={{ backgroundColor: '#fef9c3', color: '#854d0e', fontWeight: 500, fontSize: '13px', height: '24px', borderRadius: '12px' }}
            />
          )}
        </Box>
        <Typography sx={{ ...fontBase, color: '#6b7280', fontSize: '14px', mb: 3 }}>
          Registered {formatDate(user.createdAt)}
        </Typography>

        {blockedReason && (
          <Alert severity="warning" sx={{ ...fontBase, mb: 3, fontSize: '14px' }}>
            {blockedReason}
          </Alert>
        )}

        {!decided && sameName.length > 0 && (
          <Alert severity="info" sx={{ ...fontBase, mb: 3, fontSize: '14px' }}>
            Other accounts are registered under a similar company name:{' '}
            {sameName.map((d) => `${d.supplierName} (${d.email}, registration ${d.companyRegistrationNumber || 'not given'})`).join('; ')}.
            Check this is not the same company before approving.
          </Alert>
        )}

        <Section title="Applicant">
          <Field label="Name" value={fullName} />
          <Field label="Email address" value={user.email} />
          <Field label="Phone" value={user.phone} />
          <Field
            label="Email verified"
            value={user.isEmailVerified ? 'Yes' : 'No'}
          />
        </Section>

        <Section title="Company">
          <Field label="Company name" value={profile?.supplierName} />
          <Field label="Registration number" value={profile?.companyRegistrationNumber} />
          <Field label="Country of incorporation" value={profile?.registeredCountry || profile?.companyPhysicalAddress?.country} />
          <Field label="Legal nature" value={humanise(profile?.legalNature)} />
          <Field label="Entity type" value={humanise(profile?.entityType)} />
          <Field label="Company email" value={profile?.companyEmail} />
          <Field label="Website" value={profile?.companyWebsite} />
          <Field label="Physical address" value={formatAddress(profile?.companyPhysicalAddress)} xs={12} sm={12} />
        </Section>

        <Section title="Authorized contact">
          <Field label="Name" value={contact.name} />
          <Field label="Relationship to company" value={contact.relationship} />
          <Field label="ID / passport number" value={contact.idPassportNumber} />
          <Field label="Phone" value={contact.phone} />
          <Field label="Email address" value={contact.email} />
        </Section>

        {decided ? (
          <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: { xs: 2, sm: 3 } }}>
            <Typography sx={{ ...fontBase, fontWeight: 600, fontSize: '15px', color: '#111827', mb: 2 }}>
              Decision
            </Typography>
            <Grid container spacing={2.5}>
              <Field label="Outcome" value={humanise(user.supplierApprovalStatus)} />
              <Field label="Reviewed" value={formatDate(user.supplierApprovalReviewedAt)} />
              <Field
                label="Reviewed by"
                value={
                  user.supplierApprovalReviewedBy
                    ? [user.supplierApprovalReviewedBy.firstName, user.supplierApprovalReviewedBy.lastName]
                        .filter(Boolean).join(' ')
                    : ''
                }
              />
              <Field label="Comment" value={user.supplierApprovalComment} xs={12} sm={12} />
            </Grid>
          </Paper>
        ) : (
          <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: { xs: 2, sm: 3 } }}>
            <Typography sx={{ ...fontBase, fontWeight: 600, fontSize: '15px', color: '#111827', mb: 1 }}>
              Decision
            </Typography>
            <Typography sx={{ ...fontBase, color: '#6b7280', fontSize: '14px', mb: 2 }}>
              Approving starts this supplier&apos;s application and asks them to upload their
              KYC documents. Rejecting sends them the reason you give.
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Tooltip title={blockedReason}>
                <span>
                  <Button
                    variant="contained"
                    disabled={Boolean(blockedReason)}
                    onClick={() => setDecisionStatus('approved')}
                    sx={{
                      ...fontBase, textTransform: 'none', fontSize: '14px', px: 3,
                      bgcolor: '#578A18', '&:hover': { bgcolor: '#467014' },
                      '&:disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' },
                    }}
                  >
                    Approve registration
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="outlined"
                onClick={() => setDecisionStatus('rejected')}
                sx={{
                  ...fontBase, textTransform: 'none', fontSize: '14px', px: 3,
                  borderColor: '#dc2626', color: '#dc2626',
                  '&:hover': { borderColor: '#b91c1c', color: '#b91c1c', backgroundColor: '#fef2f2' },
                }}
              >
                Reject registration
              </Button>
            </Box>
          </Paper>
        )}
      </Container>

      <RegistrationDecisionDialog
        open={Boolean(decisionStatus)}
        status={decisionStatus}
        supplierName={profile?.supplierName || fullName}
        submitting={submitting}
        onCancel={() => setDecisionStatus(null)}
        onConfirm={submitDecision}
      />

      <Footer />
    </Box>
  );
};

export default RegistrationDetails;
